/**
 * Final grade pass. Runs AFTER tonemapping + sRGB conversion, so it operates on
 * display-referred colour. Combines the four cheap effects that carry most of the
 * reference sheet's mood: vignette, lift/gain grade, desaturation, and film grain.
 *
 * Chromatic aberration is applied on the sample rather than as a separate pass so
 * we only pay for one fullscreen blit.
 */
export const GradeShader = {
  name: 'GradeShader',
  uniforms: {
    tDiffuse: { value: null as unknown },
    uTime: { value: 0 },
    uVignette: { value: 0.62 },
    uVignetteSoftness: { value: 0.45 },
    uGrain: { value: 0.035 },
    uAberration: { value: 0.0018 },
    uLift: { value: [0.012, 0.018, 0.032] },
    uGain: { value: [1.0, 0.99, 0.97] },
    uSaturation: { value: 0.86 },
    /** Rises when the player is damaged; pushes the frame red and tightens vignette. */
    uHurt: { value: 0 },
    /** Rises while a security alarm is active; adds a slow red pulse at the edges. */
    uAlarm: { value: 0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    precision highp float;

    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform float uVignette;
    uniform float uVignetteSoftness;
    uniform float uGrain;
    uniform float uAberration;
    uniform vec3  uLift;
    uniform vec3  uGain;
    uniform float uSaturation;
    uniform float uHurt;
    uniform float uAlarm;

    varying vec2 vUv;

    // Cheap hash-based grain. Deliberately not tileable — it should crawl.
    float hash(vec2 p) {
      p = fract(p * vec2(443.897, 441.423));
      p += dot(p, p.yx + 19.19);
      return fract((p.x + p.y) * p.x);
    }

    void main() {
      vec2 uv = vUv;
      vec2 centered = uv - 0.5;
      float r2 = dot(centered, centered);

      // Chromatic aberration: scales with distance from centre so the middle stays sharp.
      vec2 offset = centered * uAberration * (0.35 + r2 * 2.4);
      vec3 color;
      color.r = texture2D(tDiffuse, uv + offset).r;
      color.g = texture2D(tDiffuse, uv).g;
      color.b = texture2D(tDiffuse, uv - offset).b;

      // Lift / gain grade. Lift raises the floor into blue slate (rule 1).
      color = color * uGain + uLift * (1.0 - color);

      // Desaturate everything except what is already very bright (emitters survive).
      float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      float emitterProtect = smoothstep(0.55, 0.95, luma);
      float sat = mix(uSaturation, 1.0, emitterProtect);
      color = mix(vec3(luma), color, sat);

      // Vignette.
      float vig = smoothstep(0.9, uVignetteSoftness, length(centered) * (1.0 + uVignette));
      color *= mix(1.0, vig, uVignette);

      // Alarm pulse — red bleed from the frame edges only.
      if (uAlarm > 0.001) {
        float pulse = 0.5 + 0.5 * sin(uTime * 6.2831 * 0.8);
        float edge = smoothstep(0.12, 0.5, r2);
        color += vec3(0.55, 0.03, 0.05) * edge * pulse * uAlarm;
      }

      // Damage flash — pushes the whole frame red and crushes the corners.
      if (uHurt > 0.001) {
        color = mix(color, vec3(0.42, 0.02, 0.03), uHurt * 0.55 * (0.35 + r2 * 1.6));
      }

      // Film grain, luminance-weighted so shadows stay noisy and highlights stay clean.
      float g = hash(uv * vec2(1920.0, 1080.0) + fract(uTime) * 137.0) - 0.5;
      color += g * uGrain * (1.0 - smoothstep(0.0, 0.7, luma));

      gl_FragColor = vec4(max(color, 0.0), 1.0);
    }
  `,
};
